import { Link } from "react-router-dom";
import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  pickLang,
  type UserNotificationListItem,
} from "@/services/notifications-api.service";
import {
  useNotificationMutations,
  useNotificationsList,
  useNotificationsStream,
  useUnreadNotifications,
} from "@/hooks/use-notifications";
import { cn } from "@/lib/utils";

function formatWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString("ru-RU", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export function NotificationsBell({ className }: { className?: string }) {
  useNotificationsStream(true);
  const { data: unread = 0 } = useUnreadNotifications();
  const { data: items = [], isLoading } = useNotificationsList({
    limit: 8,
    offset: 0,
  });
  const { markRead, markAllRead } = useNotificationMutations();

  const badge = unread > 99 ? "99+" : unread > 0 ? String(unread) : null;

  const onItemClick = (item: UserNotificationListItem) => {
    if (!item.read_at) {
      markRead.mutate(item.notification_id);
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn(
            "relative h-9 w-9 text-muted-foreground hover:text-foreground",
            className,
          )}
          aria-label="Уведомления"
          title="Уведомления"
        >
          <Bell className="h-4 w-4" />
          {badge && (
            <Badge
              variant="destructive"
              className="absolute -right-0.5 -top-0.5 h-4 min-w-4 px-1 text-[10px] leading-none"
            >
              {badge}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        // Как DropdownMenu в Header (z-[130]): выше тулбара workspace z-[60] и оверлеев карты до z-[120].
        className="z-[130] w-96 p-0"
      >
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-sm font-medium">Уведомления</span>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              disabled={unread === 0 || markAllRead.isPending}
              onClick={() => markAllRead.mutate()}
            >
              Прочитать все
            </Button>
            <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" asChild>
              <Link to="/notifications">Все</Link>
            </Button>
          </div>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {isLoading && (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">Загрузка…</p>
          )}
          {!isLoading && items.length === 0 && (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              Нет уведомлений
            </p>
          )}
          {items.map((item) => (
            <button
              key={item.notification_id}
              type="button"
              className={cn(
                "flex w-full flex-col gap-0.5 border-b px-3 py-2 text-left hover:bg-muted/60",
                !item.read_at && "bg-muted/30",
              )}
              onClick={() => onItemClick(item)}
            >
              <span className="text-sm font-medium leading-snug">
                {pickLang(item.title as Record<string, string>)}
              </span>
              <span className="line-clamp-2 text-xs text-muted-foreground">
                {pickLang(item.body as Record<string, string>)}
              </span>
              <span className="text-[10px] text-muted-foreground">
                {formatWhen(item.created_at)} · {item.scope}
              </span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
